import React from 'react';
import { Breadcrumbs, Link, Typography, Box } from '@mui/material';
import { NavigateNext, Home } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { COLORS } from '../../settings/themeSettings';

/**
 * Composant Breadcrumb pour la navigation contextuelle
 * Affiche le chemin de navigation actuel et permet de revenir en arrière
 */
const Breadcrumb = ({ customItems }) => {
    const navigate = useNavigate();
    const location = useLocation();

    // Mapping des chemins vers des labels lisibles
    const pathToLabel = {
        '': 'Accueil',
        'dashboard': 'Tableau de Bord',
        'etat': 'État',
        'statistiques': 'Statistiques',
        'facturation': 'Facturation',
        'unifiee': 'Facturation Unifiée',
        'nouvelle': 'Nouvelle Facture',
        'nouveau': 'Nouveau',
        'documents': 'Documents',
        'tracabilite': 'Traçabilité',
        'utilitaires': 'Utilitaires',
        'parametres-societe': 'Paramètres Société',
        'recherche': 'Recherche',
        'sage': 'Export SAGE',
        'billeterie': 'Billetterie',
        'portefeuil': 'Portefeuille Client',
        'portefeuille': 'Portefeuille Client',
        'journal': 'Journal',
        'caisse': 'Caisse',
        'paiements': 'Paiements',
        'factures': 'Factures',
        'details': 'Détails',
        'dossiers': 'Dossiers',
        'circuit': 'Circuits',
        'sejour': 'Séjours',
        'excursion': 'Excursions',
        'transfert': 'Transferts',
        'calculator': 'Calculatrice',
        'calendars': 'Calendrier',
        'documentation': 'Documentation',
        'users': 'Utilisateurs',
        'chauffeurs': 'Chauffeurs',
        'vehicules': 'Véhicules',
        'hotels': 'Hôtels'
    };

    // Générer les breadcrumbs à partir du chemin actuel
    const generateBreadcrumbs = () => {
        if (customItems) {
            return customItems;
        }

        const paths = location.pathname.split('/').filter(Boolean);
        const breadcrumbs = [
            {
                label: 'Accueil',
                path: '/',
                icon: <Home fontSize="small" />
            }
        ];

        let currentPath = '';
        paths.forEach((segment, index) => {
            // Ignorer les IDs numériques
            if (!isNaN(segment)) {
                return;
            }

            currentPath += `/${segment}`;
            const label = pathToLabel[segment] || segment.charAt(0).toUpperCase() + segment.slice(1);

            breadcrumbs.push({
                label,
                path: currentPath,
                isLast: index === paths.length - 1
            });
        });

        return breadcrumbs;
    };

    const breadcrumbs = generateBreadcrumbs();

    const handleClick = (event, path) => {
        event.preventDefault();
        navigate(path);
    };

    return (
        <Box sx={{ mb: 2 }}>
            <Breadcrumbs
                separator={<NavigateNext fontSize="small" />}
                aria-label="breadcrumb"
                sx={{
                    '& .MuiBreadcrumbs-separator': {
                        color: COLORS.gray[400]
                    }
                }}
            >
                {breadcrumbs.map((crumb, index) => {
                    const isLast = index === breadcrumbs.length - 1;

                    return isLast ? (
                        <Typography
                            key={crumb.path}
                            color="text.primary"
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                fontWeight: 600,
                                fontSize: '0.875rem'
                            }}
                        >
                            {crumb.icon && <Box component="span" sx={{ mr: 0.5, display: 'flex' }}>{crumb.icon}</Box>}
                            {crumb.label}
                        </Typography>
                    ) : (
                        <Link
                            key={crumb.path}
                            underline="hover"
                            color="inherit"
                            href={crumb.path}
                            onClick={(e) => handleClick(e, crumb.path)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                color: COLORS.gray[600],
                                fontSize: '0.875rem',
                                cursor: 'pointer',
                                '&:hover': {
                                    color: COLORS.primary
                                },
                                transition: 'color 0.2s'
                            }}
                        >
                            {crumb.icon && <Box component="span" sx={{ mr: 0.5, display: 'flex' }}>{crumb.icon}</Box>}
                            {crumb.label}
                        </Link>
                    );
                })}
            </Breadcrumbs>
        </Box>
    );
};

export default Breadcrumb;
